import { useCallback } from 'react';
import {
  activeUser,
  blockUser,
  deleteUser,
  fetchUserById,
  updateUser,
  updateUserPassword,
} from '../api/usersApi';
import extractErrorMessage from '../../../shared/utils/extractErrorMessage';

const useUsersHandlers = ({
  deleteUserId,
  deletePassword,
  loadUsers,
  setDeleteError,
  setDeletePassword,
  setDeleteUser,
  setDone,
  setIsCreateUserModalOpen,
  setIsDeleteLoading,
  setIsDeleteModalOpen,
  setIsUpdateUserModalOpen,
  setMessage,
  setUpdateLoading,
  setUserForUpdate,
  setUsers,
  userForUpdate,
}) => {
  const handleCreateUserModalClose = useCallback(() => {
    setIsCreateUserModalOpen(false);
    setDone(false);
  }, [setDone, setIsCreateUserModalOpen]);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteUser(null);
    setDeletePassword('');
    setDeleteError('');
    setDone(false);
  }, [setDeleteError, setDeletePassword, setDeleteUser, setDone, setIsDeleteModalOpen]);

  const handleUpdateModalClose = useCallback(() => {
    setIsUpdateUserModalOpen(false);
    setUserForUpdate(null);
    setDone(false);
  }, [setDone, setIsUpdateUserModalOpen, setUserForUpdate]);

  const handleUpdateUser = useCallback(
    async (userId) => {
      setUpdateLoading(true);
      try {
        const { data, ok, message } = await fetchUserById(userId);
        const userData =
          data?.user || data?.data?.user || data?.data || data || null;

        if (!ok || !userData) {
          setMessage(message || 'Greska pri ucitavanju korisnika');
          return;
        }

        setUserForUpdate(userData);
        setIsUpdateUserModalOpen(true);
        setDone(false);
      } finally {
        setUpdateLoading(false);
      }
    },
    [
      setDone,
      setIsUpdateUserModalOpen,
      setMessage,
      setUpdateLoading,
      setUserForUpdate,
    ]
  );

  const handleUpdateUserSubmit = useCallback(
    async (formData) => {
      if (!userForUpdate?._id) {
        return { ok: false, message: 'Korisnik nije izabran.' };
      }

      setUpdateLoading(true);
      try {
        const { data, ok, message, status, code } = await updateUser(
          userForUpdate._id,
          formData,
          userForUpdate
        );

        if (!ok) {
          setMessage(message);
          return { ok: false, message, status, code };
        }

        const updatedUser =
          data?.user || data?.data?.user || data?.data || data || null;
        if (updatedUser) {
          setUserForUpdate(updatedUser);
        }

        setMessage(message || 'Korisnik uspesno azuriran.');
        loadUsers();
        return { ok: true, message: message || 'Korisnik uspesno azuriran.' };
      } finally {
        setUpdateLoading(false);
      }
    },
    [loadUsers, setMessage, setUpdateLoading, setUserForUpdate, userForUpdate]
  );

  const handleUpdateUserPasswordSubmit = useCallback(
    async (password) => {
      if (!userForUpdate?._id) {
        return { ok: false, message: 'Korisnik nije izabran.' };
      }

      setUpdateLoading(true);
      try {
        const { data, ok, message } = await updateUserPassword(
          userForUpdate._id,
          password
        );

        if (!ok) {
          setMessage(message);
          return { ok: false, message };
        }

        const updatedUser =
          data?.user || data?.data?.user || data?.data || data || null;
        if (updatedUser) {
          setUserForUpdate(updatedUser);
        }

        setMessage(message || 'Lozinka korisnika uspesno azurirana.');
        loadUsers();
        return {
          ok: true,
          message: message || 'Lozinka korisnika uspesno azurirana.',
        };
      } finally {
        setUpdateLoading(false);
      }
    },
    [loadUsers, setMessage, setUpdateLoading, setUserForUpdate, userForUpdate]
  );
  const handleActiveUser = useCallback(
    async (activeUserId) => {
      try {
        const { ok, message } = await activeUser(activeUserId);

        if (!ok) {
          setMessage(message);
          return;
        }

        setMessage(message || 'Korisnik aktiviran');
        loadUsers();
      } catch (error) {
        const message = extractErrorMessage(
          error,
          'Doslo je do greske pri aktiviranju korisnika.'
        );
        return {
          data: null,
          status: error?.response?.status || null,
          ok: false,
          message: message,
        };
      }
    },
    [loadUsers, setMessage]
  );

  const handleBlockUser = useCallback(
    async (blockedUserId) => {
      setIsDeleteLoading(true);
      try {
        const { ok, message } = await blockUser(blockedUserId);

        if (!ok) {
          setMessage(message);
          return { ok: false, message };
        }

        setMessage(message || 'Korisnik arhiviran');
        loadUsers();
        return { ok: true, message: message || 'Korisnik arhiviran' };
      } finally {
        setIsDeleteLoading(false);
      }
    },
    [loadUsers, setIsDeleteLoading, setMessage]
  );

  const handleDeleteUser = useCallback(async () => {
    const userId = deleteUserId;
    if (!userId) return;
    if (!deletePassword?.trim()) {
      setDeleteError('Unesite svoj PIN.');
      return;
    }

    setIsDeleteLoading(true);
    try {
      setDeleteError('');
      const { ok, message } = await deleteUser(userId, deletePassword.trim());

      if (!ok) {
        setDeleteError(message || 'Doslo je do greske pri trajnoj deaktivaciji.');
        setMessage(message);
        return;
      }

      setUsers((prev) => prev.filter((user) => user._id !== userId));
      setMessage(message || 'Korisnik trajno deaktiviran');
      loadUsers();
      setDone(true);
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setDeletePassword('');
        setDeleteError('');
        setDeleteUser(null);
      }, 1000);
    } finally {
      setIsDeleteLoading(false);
    }
  }, [
    deletePassword,
    deleteUserId,
    loadUsers,
    setDeleteError,
    setDeletePassword,
    setDeleteUser,
    setDone,
    setIsDeleteLoading,
    setIsDeleteModalOpen,
    setMessage,
    setUsers,
  ]);

  return {
    handleActiveUser,
    handleBlockUser,
    handleCreateUserModalClose,
    handleDeleteModalClose,
    handleDeleteUser,
    handleUpdateModalClose,
    handleUpdateUser,
    handleUpdateUserPasswordSubmit,
    handleUpdateUserSubmit,
  };
};

export default useUsersHandlers;
